import React, { useState, useEffect } from 'react';
import { FieldArray, useFormikContext, useField } from 'formik';
import IconButton from '../../buttons/IconButton';
import EditItem from '../SimpleForm/EditItem';
import classNames from '../../../utility/classNames';
import Tooltip from '../../Tooltip';
import renderValue from '../utility/renderValue';
import InputTypeSwitch from './InputTypeSwitch';
import { getMaxLengthArray } from '../utility/getBeforeAfterData';
import { isEqual } from 'lodash';
import { checkCondition } from '../utility/filterSchema';
import FormUIOptions from './formUIOptions';
import { formatValue } from '../utility/renderValue';

// This component is used to render a table with a single row for each item in the array, with a button to add new items. Each component will come with a modal to edit.
// For now this is only used in the Rent Details for the rehab worksheet.
export default function BeforeAfterTableModal({
  name,
  label,
  description,
  sectionHeaders = [],
  tooltipHeading,
  tooltipFormula,
  modalDescription,
  itemName,
  tableSchema,
  preValue,
  externalData = {},
  condition,
}) {
  // Formik populates FieldArray with the array from initialData, so we can access it here through context
  const { values: afterValues, setFieldTouched } = useFormikContext();
  const [field, meta, helpers] = useField(name);
  const { setValue } = helpers;
  const tableSchemaFiltered = tableSchema.filter((field) => field.after);

  useEffect(() => {
    if (preValue) {
      setValue(preValue);
    }
  }, [preValue]);

  const beforeDataArray = externalData[name] || [];
  const afterDataArray = afterValues[name] || [];

  useEffect(() => {
    if (name !== 'grossIncomePerUnit') return;

    let newAfterDataArray = [];

    afterDataArray.forEach((afterItem, index) => {
      const beforeItem = beforeDataArray?.[index];
      if (beforeItem && afterItem) {
        if (beforeItem.rent?.value !== afterItem.beforeRent?.value) {
          newAfterDataArray.push({
            ...afterItem,
            beforeRent: beforeItem.rent,
          });
        } else {
          newAfterDataArray.push(afterItem);
        }
      } else {
        newAfterDataArray.push(afterItem);
      }
    });

    if (!isEqual(newAfterDataArray, afterDataArray)) {
      setValue(newAfterDataArray);
    }
  }, [beforeDataArray, afterDataArray]);

  const emptyArrayMaxLength = getMaxLengthArray(
    beforeDataArray,
    afterDataArray
  );

  // Edit modal state
  const [addItem, setAddItem] = useState(null);
  const [editItem, setEditItem] = useState(null); // {item, index}

  const handleSave = (helper, item) => {
    setFieldTouched(name, true);
    if (addItem) {
      helper.push(item);
      setAddItem(null);
    }
    if (editItem) {
      helper.replace(editItem.index, item);
      setEditItem(null);
    }
  };

  const handleDelete = (helper, index) => {
    setFieldTouched(name, true);
    helper.remove(index);
  };

  return (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <div className="mb-4">
          <span className="text-md font-medium text-neutral-900 block mb-2 mt-2">
            {label}{' '}
            {description && (
              <Tooltip
                text={description}
                formula={tooltipFormula}
                heading={tooltipHeading}
                type="info"
              />
            )}
          </span>
          {Array.isArray(emptyArrayMaxLength) &&
            emptyArrayMaxLength.map((_, index) => {
              const field = tableSchemaFiltered[1];
              const beforeItem = beforeDataArray[index];
              //render before value only if condition is not present or if condition is present and it is true, using the original / 'before' data
              const shouldShowBeforeValue =
                !condition || checkCondition(condition, externalData);
              const beforeValue =
                beforeItem && shouldShowBeforeValue
                  ? renderValue(field, { ...externalData, ...beforeItem })
                  : null;
              const beforeName =
                beforeItem && shouldShowBeforeValue
                  ? renderValue(tableSchemaFiltered[0], beforeItem)
                  : null;

              const afterItem = afterDataArray?.[index];
              const afterName =
                afterDataArray?.[index]?.[tableSchemaFiltered[0].name];

              const displayOriginalHeader =
                beforeValue &&
                beforeName &&
                field.type !== FormUIOptions.YesNo &&
                field.type !== FormUIOptions.Header &&
                field.type !== FormUIOptions.CalculatedSection &&
                field.type !== FormUIOptions.Calculated;
              const displayAfterHeader =
                field.type !== FormUIOptions.YesNo &&
                field.type !== FormUIOptions.Header &&
                field.type !== FormUIOptions.CalculatedSection &&
                field.type !== FormUIOptions.Calculated;

              return (
                <React.Fragment key={`${tableSchemaFiltered[0].name}-${index}`}>
                  {/* {!beforeValue &&
                    !beforeName &&
                    beforeDataArray[index - 1] && (
                      <hr className="mt-4 mb-2 border-neutral-200" />
                    )} */}
                  <div className="grid sm:grid-cols-4-beforeaftertablemodal sm:items-center sm:justify-center sm:space-y-2">
                    {displayOriginalHeader && (
                      <p className="sm:hidden text-base font-medium text-neutral-400 uppercase tracking-wider flex-grow mb-3">
                        {Array.isArray(sectionHeaders[0]) ? (
                          <>
                            <span className="inline-block whitespace-nowrap">
                              {sectionHeaders[0][0]} &#47;
                            </span>
                            <span className="inline-block">
                              &nbsp;{sectionHeaders[0][1]}:
                            </span>
                          </>
                        ) : (
                          `${sectionHeaders[0].heading}:`
                        )}
                      </p>
                    )}
                    <div
                      className={classNames(
                        'h-full flex flex-col items-start justify-center pb-4 sm:pb-1',
                        !beforeValue && !beforeName && 'pt-2'
                      )}
                    >
                      <p
                        className={classNames(
                          'text-base text-neutral-500 -mt-2',
                          tableSchemaFiltered.length > 1 ? '' : 'ml-2',
                          !beforeValue && !beforeName && 'text-neutral-900'
                        )}
                      >
                        {afterName || beforeName}&nbsp;
                      </p>
                      <p className="sm:w-full sm:col-span-1 text-left text-md text-neutral-900">
                        {beforeValue && (
                          <span className="rounded border p-[2px] border-neutral-50">
                            {beforeValue}
                          </span>
                        )}
                        {!beforeValue && !beforeName && (
                          <IconButton
                            icon="delete"
                            classes="focus:outline-none focus:ring-1 focus:ring-warning hover:text-warning focus:text-warning sm:p-0 "
                            style="modal"
                            iconClasses="h-[17px] w-[17px]"
                            handleClick={() =>
                              handleDelete(arrayHelpers, index)
                            }
                          />
                        )}
                      </p>
                    </div>
                    <p className="sm:hidden text-base font-medium text-neutral-400 uppercase tracking-wider flex-grow mb-1">
                      {Array.isArray(sectionHeaders[1]) ? (
                        <>
                          <span className="inline-block whitespace-nowrap">
                            {sectionHeaders[1][0]} &#47;
                          </span>
                          <span className="inline-block">
                            &nbsp;{sectionHeaders[1][1]}:
                          </span>
                        </>
                      ) : (
                        `${sectionHeaders[1].heading}:`
                      )}
                    </p>

                    <div className="relative w-full pr-2 col-span-1 flex justify-end items-center pb-3 sm:pb-0">
                      {tableSchemaFiltered.length > 2 && (
                        <>
                          {/* <p className="text-base flex items-center justify-between">
                            {formatValue(item, 1)}
                          </p> */}
                          <div
                            className="flex pt-1"
                            onClick={() => {
                              if (!afterItem.unitRehab) {
                                setEditItem({
                                  afterItem: { ...afterItem, unitRehab: true },
                                  index,
                                });
                              }
                            }}
                          >
                            <InputTypeSwitch
                              elem={{
                                ...tableSchemaFiltered[2],
                                name: `${name}.${index}.${tableSchemaFiltered[2].name}`, // this allows Formik to tell apart the same field in different array items
                                layout: 'tableNoLabel',
                              }}
                              externalData={{ ...externalData, ...afterValues }}
                            />
                          </div>
                        </>
                      )}
                    </div>
                    {displayAfterHeader && (
                      <p className="sm:hidden text-base font-medium text-neutral-400 uppercase tracking-wider flex-grow mb-1">
                        {Array.isArray(sectionHeaders[2]) ? (
                          <>
                            <span className="inline-block whitespace-nowrap">
                              {sectionHeaders[2][0]} &#47;
                            </span>
                            <span className="inline-block">
                              &nbsp;{sectionHeaders[2][1]}:
                            </span>
                          </>
                        ) : (
                          `${sectionHeaders[2].heading}:`
                        )}
                      </p>
                    )}
                    <div
                      className={classNames(
                        'flex items-center justify-between col-span-1'
                      )}
                    >
                      <div className="relative sm:max-w-sm w-full px-0 mt-1">
                        {tableSchemaFiltered.length > 1 && (
                          <>
                            <InputTypeSwitch
                              elem={{
                                ...tableSchemaFiltered[1],
                                name: `${name}.${index}.${tableSchemaFiltered[1].name}`, // this allows Formik to tell apart the same field in different array items
                                layout: 'tableNoLabel',
                              }}
                              externalData={{ ...externalData, ...afterValues }}
                            />
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-1 justify-start sm:justify-end items-center mb-4 sm:mb-0">
                      <IconButton
                        icon="edit"
                        style="modal"
                        iconClasses="h-4 w-4"
                        handleClick={() => setEditItem({ afterItem, index })}
                      />
                    </div>
                  </div>
                </React.Fragment>
              );
            })}

          <div
            className={classNames(
              'flex items-center justify-end',
              emptyArrayMaxLength?.length ? 'mt-10' : 'mt-4'
            )}
          >
            <div className="col-span-2"> </div>
            <IconButton
              text={`Add ${itemName || 'item'}`}
              icon="plus"
              iconPosition="left"
              classes={'min-w-[14rem]'}
              style="outline"
              handleClick={(e) => {
                e.stopPropagation();
                setAddItem({});
              }}
              iconClasses="text-sm mb-[2px]"
            />
          </div>

          {/* Modal
           */}
          {(addItem || editItem) && (
            <EditItem
              title={`${addItem ? 'Add' : 'Edit'} ${itemName || 'item'}`}
              modalDescription={modalDescription}
              tableSchema={tableSchemaFiltered}
              beforeArray={beforeDataArray}
              arrayHelpers={arrayHelpers}
              handleSave={handleSave}
              listItem={addItem ? addItem : editItem}
              setItem={addItem ? setAddItem : setEditItem}
            />
          )}
        </div>
      )}
    />
  );
}
